import { Icon } from '@iconify/react';
import {
  Button,
  Card,
  ScrollShadow,
  Tab,
  Tabs,
  Tooltip,
} from '@nextui-org/react';
import { useState } from 'react';

interface ActionCardProps {
  title: string;
  content: string;
  icon: string;
  onClick: () => void;
}

const ActionCard = ({ title, content, icon, onClick }: ActionCardProps) => (
  <Card
    className="p-4 bg-white dark:bg-default-100 cursor-pointer hover:bg-default-100 transition-colors gap-1 border-1"
    isPressable
    onPress={onClick}
    shadow="none"
  >
    <div className="flex gap-2">
      <Button
        isIconOnly
        variant="solid"
        size="sm"
        aria-label="Start"
        className="bg-white border-1"
      >
        <Icon icon={icon} width="20" />
      </Button>
      <div className="flex flex-col text-start gap-1 pl-2">
        <h3 className="font-semibold text-xs">{title}</h3>
        <p className="text-xs text-default-600">{content}</p>
      </div>
    </div>
  </Card>
);

interface ChatSidepanelProps {
  onActionClick?: (text: string) => void;
  hasMessages?: boolean;
  actionTitle?: string;
  userProfile?: any;
}

export function ChatSidepanel({
  onActionClick,
  hasMessages = false,
  actionTitle,
  userProfile,
}: ChatSidepanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [selected, setSelected] = useState('actions');

  const handleActionClick = (text: string) => {
    onActionClick?.(text);
  };

  if (!hasMessages) return null;

  if (isCollapsed) {
    return (
      <div className="relative flex border-l border-divider mt-1">
        <Tooltip content="Open Quick Actions" placement="right">
          <button
            onClick={() => setIsCollapsed(false)}
            className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 p-1 rounded-full bg-white border border-divider shadow-sm hover:bg-default-100 transition-colors"
            aria-label="Expand quick actions panel"
          >
            <Icon icon="mdi:chevron-left" className="w-4 h-4" />
          </button>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="relative flex">
      <Tooltip content="Close Quick Actions" placement="right">
        <button
          onClick={() => setIsCollapsed(true)}
          className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 p-1 rounded-full bg-white border border-divider shadow-sm hover:bg-default-100 transition-colors"
        >
          <Icon icon="mdi:chevron-right" className="w-4 h-4" />
        </button>
      </Tooltip>

      <div className="w-80 flex flex-col border-l border-divider bg-white transition-width duration-300">
        <div className="w-full flex items-center justify-center px-4 py-2 border-b border-divider">
          <Tabs
            aria-label="Tabs sizes"
            size="sm"
            onSelectionChange={(key) => setSelected(key as string)}
          >
            <Tab key="actions" title="Actions" />
            <Tab key="activity" title="Activity" />
            {/* <Tab key="videos" title="Videos" /> */}
          </Tabs>
          {/* <Button
            isIconOnly
            variant="light"
            onPress={() => setIsCollapsed(true)}
          >
            <Icon icon="mdi:chevron-right" className="w-5 h-5" />
          </Button> */}
        </div>

        <ScrollShadow className="flex-1 p-4 space-y-2">
          {selected === 'actions' && (
            <div>
              <ActionCard
                title="Summary"
                content="Generate a summary of our conversation so far."
                icon="mdi:text-box-edit-outline"
                onClick={() =>
                  handleActionClick(
                    'Can you provide a summary of our conversation so far?'
                  )
                }
              />
            </div>
          )}
          {selected === 'activity' && userProfile && (
            <div className="flex flex-col gap-3 p-4 rounded border-1">
              <p className="text-sm font-bold">My Profile</p>
              <p className="text-xs leading-4 line-clamp-6">
                {userProfile?.value}
              </p>
              <p className="text-xs text-black-200 italic">
                Created at: {new Date(userProfile?.created_at).toLocaleString()}
              </p>
            </div>
          )}

          {/* <ActionCard
            title="Insights"
            content="Extract key insights from our discussion."
            icon="bx:bulb"
            onClick={() =>
              handleActionClick(
                'What are the key insights from our discussion?'
              )
            }
          />

          <ActionCard
            title="Action Items"
            content="List all action items from our conversation."
            icon="mdi:checkbox-marked-circle-outline"
            onClick={() =>
              handleActionClick(
                'Can you list all the action items from our conversation?'
              )
            }
          />

          <ActionCard
            title="Sentiment Analysis"
            content="Analyze the tone and sentiment of our conversation."
            icon="mdi:chart-line"
            onClick={() =>
              handleActionClick(
                'Can you analyze the sentiment and tone of our conversation?'
              )
            }
          /> */}
        </ScrollShadow>
      </div>
    </div>
  );
}
